import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  useToast,
  useColorModeValue,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { getCourseById, Course } from '../services/university-service';
import CoursePlayer from '../components/university/CoursePlayer';
import ContentSidebar from '../components/university/ContentSidebar';
import { useParams, useNavigate } from 'react-router-dom';
import { PARTNER_ROUTES } from '@bemap/utils';
import { useTranslation } from 'react-i18next';

export default function University() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [courseNotFound, setCourseNotFound] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    if (courseId) {
      loadCourseById(parseInt(courseId));
    }
  }, [courseId]);

  const loadCourseById = async (id: number) => {
    try {
      setLoading(true);
      setCourseNotFound(false);
      const response = await getCourseById(id);
      if (response.statusCode === 200) {
        const course = response.message as Course;
        setSelectedCourse(course);
      } else {
        setCourseNotFound(true);
        toast({
          title: t('university.courses.course_not_found'),
          description: t('general.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      setCourseNotFound(true);
      toast({
        title: t('university.courses.error_loading_courses'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCourses = () => {
    navigate(PARTNER_ROUTES.STUDENT_DASHBOARD);
  };

  const handleContentComplete = (contentId: number) => {
    // Handle content completion if needed
    console.log('Content completed:', contentId);
  };

  const handleQuizSubmit = (contentId: number, answers: any) => {
    // Handle quiz submission if needed
    console.log('Quiz submitted:', contentId, answers);
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  // Show 404 if course not found
  if (courseNotFound || !selectedCourse) {
    return (
      <Box p={6} minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center">
        <Box textAlign="center">
          <Box fontSize="6xl" fontWeight="bold" color="gray.400" mb={4}>
            404
          </Box>
          <Box fontSize="xl" fontWeight="semibold" mb={2}>
            {t('not_found.title')}
          </Box>
          <Box color="gray.600" mb={6}>
            {t('not_found.description')}
          </Box>
          <Box
            as="button"
            bg="blue.500"
            color="white"
            px={6}
            py={3}
            borderRadius="md"
            _hover={{ bg: 'blue.600' }}
            onClick={() => navigate(PARTNER_ROUTES.STUDENT_DASHBOARD)}
          >
            {t('not_found.back_to_dashboard')}
          </Box>
        </Box>
      </Box>
    );
  }

  // If course is selected, show video player and content sidebar
  return (
    <Flex direction={{ base: 'column', lg: 'row' }} gap={4} minH="100vh">
      <Box flex={2} minW={0}>
        <CoursePlayer
          onBack={handleBackToCourses}
          onContentComplete={handleContentComplete}
          onQuizSubmit={handleQuizSubmit}
        />
      </Box>
      <Box flex={1} minW="320px">
        <ContentSidebar course={selectedCourse} />
      </Box>
    </Flex>
  );
} 